import { documentGetUploadSignedUrl } from "@/proofingService-client/sdk.gen";

/**
 * Service for handling document upload operations
 */

/**
 * Gets a signed URL for uploading a document to S3
 * @param params - Upload parameters
 * @param params.proofingTitleId - The ID of the proofing title
 * @param params.proofingTitleName - The title/name of the proofing title
 * @param params.roundId - The ID of the proofing round
 * @param params.filename - The name of the file to upload
 * @returns Promise resolving to the signed URL and document path
 */
export const getDocumentUploadSignedUrl = async ({
  proofingTitleId,
  proofingTitleName,
  roundId,
  filename,
}: {
  proofingTitleId: string;
  proofingTitleName: string;
  roundId: string;
  filename: string;
}): Promise<{ url: string; documentPath: string }> => {
  const documentPath = `/${proofingTitleId}-${proofingTitleName}/${roundId}/${filename}`;

  const response = await documentGetUploadSignedUrl({
    query: { path: documentPath },
  });

  if (!response.data?.url) {
    throw new Error("Failed to get signed upload URL");
  }

  return {
    url: response.data.url,
    documentPath,
  };
};

/**
 * Uploads a file to S3 using a signed URL
 * @param signedUrl - The signed URL from getDocumentUploadSignedUrl
 * @param file - The file to upload
 * @returns Promise resolving when upload is complete
 */
export const uploadFileToS3 = async (signedUrl: string, file: File): Promise<void> => {
  const response = await fetch(signedUrl, {
    method: "PUT",
    body: file,
    headers: {
      "Content-Type": file.type,
    },
  });

  if (!response.ok) {
    throw new Error(`File upload failed: ${response.statusText}`);
  }
};
